import { useMemo } from 'react';
import { Button, IconExternalLink, Tooltip } from '@screentone/core';

import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { appEnv, buildCMSUrl, buildLiveCoverageUrl, getContentEnv } from 'utils/cms';

interface OpenInCMSProps {
  content: AllessehContent;
}

const OpenInCMSButton = ({
  onClick,
  dataTestId,
  disabled
}: {
  onClick: () => void;
  dataTestId?: string;
  disabled?: boolean;
}) => (
  <Button
    data-testid={dataTestId ?? 'newspress-button-open-cms-button'}
    tertiary
    icon={IconExternalLink}
    iconPosition="right"
    onClick={onClick}
    disabled={disabled}
  >
    Open in CMS
  </Button>
);

const OpenInCMSTooltip = ({
  onClick,
  dataTestId,
  disabled,
  message
}: {
  onClick: () => void;
  dataTestId?: string;
  message?: string;
  disabled?: boolean;
}) => (
  <Tooltip data-testid="edit-alt-summ-option-tooltip">
    <OpenInCMSButton onClick={onClick} dataTestId={dataTestId} disabled={disabled} />
    {disabled && message ? (
      <Tooltip.Content
        data-testid="edit-alt-summ-option-tooltip-content"
        id="altSumModalTooltip"
        position="bottom"
        size="lg"
      >
        {message}
      </Tooltip.Content>
    ) : null}
  </Tooltip>
);

export const OpenInCMS = ({ content }: OpenInCMSProps) => {
  const cmsUrl = useMemo(
    () =>
      buildCMSUrl(
        content.data.id,
        content.data.attributes.type,
        content.data.attributes.source_url,
        content.data.attributes.product
      ),
    [content.data.id, content.data.attributes.type, content.data.attributes.source_url, content.data.attributes.product]
  );

  const liveCoverageUrl = useMemo(() => {
    const lcAltSumUrl = buildLiveCoverageUrl(
      content.data.attributes.source_url,
      content.data.attributes.associations?.root_id ?? ''
    );
    return content.data.attributes.section_type !== 'stack' && lcAltSumUrl ? `${lcAltSumUrl}/stream` : lcAltSumUrl;
  }, [
    content.data.attributes.section_type,
    content.data.attributes.source_url,
    content.data.attributes.associations?.root_id
  ]);

  const handleViewCMSUrl = () => {
    if (!cmsUrl) return;
    window.open(cmsUrl, '_blank');
  };

  const handleViewLiveCoverageUrl = () => {
    if (!liveCoverageUrl) return;
    window.open(liveCoverageUrl, '_blank');
  };

  const isLiveCoverage = content.data.attributes.product.includes('Live Coverage');
  const tooltipMessage = `Disabled: The article belongs to the ${getContentEnv(
    content.data.attributes.source_url
  )} environment, which does not match the current ${appEnv} environment.`;

  return cmsUrl ? (
    // If has newspress URL, open in CMS
    <OpenInCMSButton onClick={handleViewCMSUrl} dataTestId="newspress-button-open-cms-button" />
  ) : isLiveCoverage ? (
    <OpenInCMSTooltip
      onClick={handleViewLiveCoverageUrl}
      dataTestId={
        !liveCoverageUrl ? 'livecoverage-button-open-cms-button-disabled' : 'livecoverage-button-open-cms-button'
      }
      disabled={!liveCoverageUrl}
      message={tooltipMessage}
    />
  ) : null;
};
